import { supabase } from './supabase';

const BRAND_COLOR   = '#e8a4b8';
const BRAND_DARK    = '#c97a97';
const TEXT_COLOR    = '#2c3e38';
const SAGE_COLOR    = '#6b8262';
const SAGE_LIGHT    = '#e8ede6';
const BG_COLOR      = '#fcfbf9';
const ADMIN_EMAIL   = 'hi@arizonaroses.com';

// ─── Base template ────────────────────────────────────────────────────────────
const baseTemplate = (content, preheader = '') => `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Arizona Roses</title>
  <!--[if mso]><noscript><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch></o:OfficeDocumentSettings></xml></noscript><![endif]-->
</head>
<body style="margin:0;padding:0;background-color:${BG_COLOR};font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;">
  ${preheader ? `<div style="display:none;max-height:0;overflow:hidden;color:${BG_COLOR};">${preheader}</div>` : ''}
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:${BG_COLOR};padding:40px 16px;">
    <tr><td align="center">
      <table width="100%" cellpadding="0" cellspacing="0" border="0" style="max-width:600px;">

        <!-- Header -->
        <tr>
          <td style="background:linear-gradient(135deg,${BRAND_COLOR} 0%,${BRAND_DARK} 100%);border-radius:20px 20px 0 0;padding:36px 40px;text-align:center;">
            <div style="display:inline-block;background:rgba(255,255,255,0.15);border-radius:50px;padding:6px 20px;margin-bottom:14px;">
              <span style="color:rgba(255,255,255,0.9);font-size:10px;letter-spacing:3px;text-transform:uppercase;font-weight:700;">Est. in Arizona</span>
            </div>
            <h1 style="margin:0;color:white;font-size:32px;font-family:Georgia,'Times New Roman',serif;font-weight:400;letter-spacing:1px;">Arizona Roses</h1>
            <p style="margin:6px 0 0;color:rgba(255,255,255,0.8);font-size:13px;letter-spacing:1px;">✦ Hand-crafted arrangements ✦</p>
          </td>
        </tr>

        <!-- Body -->
        <tr>
          <td style="background:white;padding:44px 48px;border-left:1px solid #ede9e4;border-right:1px solid #ede9e4;">
            ${content}
          </td>
        </tr>

        <!-- Footer -->
        <tr>
          <td style="background:${BG_COLOR};border:1px solid #ede9e4;border-top:none;border-radius:0 0 20px 20px;padding:24px 40px;text-align:center;">
            <p style="margin:0 0 6px;font-size:11px;color:#aab8a5;text-transform:uppercase;letter-spacing:2px;">Arizona Roses &mdash; Surprise, AZ</p>
            <p style="margin:0 0 12px;font-size:11px;color:#c4b8b0;">
              Questions? Reply to this email or reach us at
              <a href="mailto:hi@arizonaroses.com" style="color:${BRAND_COLOR};text-decoration:none;font-weight:600;">hi@arizonaroses.com</a>
            </p>
            <p style="margin:0;font-size:10px;color:#c8c0bc;">&copy; ${new Date().getFullYear()} Arizona Roses. All rights reserved.</p>
          </td>
        </tr>

      </table>
    </td></tr>
  </table>
</body>
</html>
`;

// ─── Shared sub-components ────────────────────────────────────────────────────
const orderSummaryBox = (order, item) => `
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background:${BG_COLOR};border-radius:14px;border:1px solid #ede9e4;margin:24px 0;">
    <tr><td style="padding:20px 24px;">
      <p style="margin:0 0 12px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:2px;color:${SAGE_COLOR};">Order Summary</p>
      ${row('Order ID', '#' + order.id.substring(0, 8).toUpperCase())}
      ${row('Design', item.design_name || 'Custom Arrangement')}
      ${row('Rose Color', item.color_preference || '—')}
      ${row('Delivery Date', new Date(order.delivery_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }))}
      ${row('Recipient', order.recipient_name)}
    </td></tr>
  </table>
`;

const row = (label, value) => `
  <p style="margin:6px 0;font-size:14px;color:${TEXT_COLOR};">
    <span style="color:#9aab94;font-weight:600;display:inline-block;min-width:120px;">${label}</span>
    <span>${value}</span>
  </p>
`;

const ctaButton = (href, text) => `
  <div style="text-align:center;margin:32px 0 8px;">
    <a href="${href}" style="display:inline-block;background:linear-gradient(135deg,${BRAND_COLOR} 0%,${BRAND_DARK} 100%);color:white;padding:15px 40px;border-radius:50px;text-decoration:none;font-size:13px;font-weight:700;letter-spacing:2px;text-transform:uppercase;box-shadow:0 4px 16px rgba(232,164,184,0.4);">${text}</a>
  </div>
`;

const divider = () => `<hr style="border:none;border-top:1px solid #ede9e4;margin:28px 0;" />`;

const quoteBlock = (text) => `
  <div style="border-left:3px solid ${BRAND_COLOR};padding:14px 20px;background:#fdf8fa;border-radius:0 12px 12px 0;margin:20px 0;">
    <p style="margin:0;color:${SAGE_COLOR};font-style:italic;font-size:14px;line-height:1.7;">${text}</p>
  </div>
`;


// ─── sendEmail helper ─────────────────────────────────────────────────────────
export const sendEmail = async (to, subject, html) => {
  try {
    const { data, error } = await supabase.functions.invoke('send-email', {
      body: { to, subject, html }
    });
    if (error) throw error;
    return data;
  } catch (err) {
    console.error('Failed to send email:', err);
    throw err;
  }
};


// ─── Email templates ──────────────────────────────────────────────────────────
export const emailTemplates = {

  // ── 1. Order confirmed (auto at checkout) ────────────────────────────────
  orderReceived: (order, item) => baseTemplate(`
    <h2 style="font-family:Georgia,serif;font-size:26px;color:${TEXT_COLOR};margin:0 0 8px;font-weight:400;">Your order is confirmed! 🌹</h2>
    <p style="margin:0 0 4px;font-size:15px;color:${SAGE_COLOR};">Thank you for choosing Arizona Roses, <strong style="color:${TEXT_COLOR};">${order.customer_name}</strong>.</p>
    <p style="font-size:14px;color:#8a9a85;margin:0 0 20px;">Your payment has been processed and we're already getting to work.</p>

    ${orderSummaryBox(order, item)}

    ${quoteBlock('Every arrangement is crafted by hand. Once yours is ready, we\'ll send you a photo before it heads out for delivery.')}

    ${ctaButton('https://order.arizonaroses.com/#/portal', 'Track Your Order')}
  `, `Your Arizona Roses order #${order.id.substring(0,8).toUpperCase()} is confirmed!`),


  // ── 2. Out for delivery (photo included) ─────────────────────────────────
  outForDelivery: (order, photoUrl, designName) => baseTemplate(`
    <h2 style="font-family:Georgia,serif;font-size:26px;color:${TEXT_COLOR};margin:0 0 8px;font-weight:400;">Your arrangement is on its way! 🚗</h2>
    <p style="font-size:14px;color:#8a9a85;margin:0 0 24px;">We've finished your <strong style="color:${TEXT_COLOR};">${designName}</strong> for <strong style="color:${TEXT_COLOR};">${order.recipient_name}</strong> — here's a sneak peek.</p>

    ${photoUrl ? `
    <div style="margin:0 0 28px;text-align:center;">
      <img src="${photoUrl}" alt="Your arrangement" style="width:100%;max-width:440px;border-radius:18px;box-shadow:0 12px 40px rgba(0,0,0,0.10);border:4px solid white;" />
    </div>` : ''}

    <div style="background:linear-gradient(135deg,#fdf4f7 0%,#f6f9f5 100%);border-radius:14px;padding:20px 24px;margin:0 0 24px;">
      <p style="margin:0;font-size:14px;color:${TEXT_COLOR};">
        🗓 <strong>Delivery today:</strong>
        ${new Date(order.delivery_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
      </p>
      <p style="margin:8px 0 0;font-size:13px;color:${SAGE_COLOR};">📍 ${order.delivery_address}, ${order.delivery_city}, AZ ${order.delivery_zip}</p>
    </div>

    ${quoteBlock('Our driver will handle your arrangement with care. If you need to reach us before delivery, just reply to this email.')}

    ${ctaButton('https://order.arizonaroses.com/#/portal', 'View Order Details')}
  `, `Your roses are on their way to ${order.recipient_name}!`),


  // ── 3. Delivered ─────────────────────────────────────────────────────────
  orderDelivered: (order) => baseTemplate(`
    <h2 style="font-family:Georgia,serif;font-size:26px;color:${TEXT_COLOR};margin:0 0 8px;font-weight:400;">Delivered with love 💐</h2>
    <p style="font-size:14px;color:#8a9a85;margin:0 0 24px;">Your arrangement has been hand-delivered to <strong style="color:${TEXT_COLOR};">${order.recipient_name}</strong>. We hope it made their day unforgettable.</p>

    <div style="text-align:center;padding:28px 20px;background:linear-gradient(135deg,#fdf4f7 0%,#f6f9f5 100%);border-radius:16px;margin:0 0 28px;">
      <div style="font-size:48px;margin-bottom:12px;">🌹</div>
      <p style="margin:0;font-family:Georgia,serif;font-size:18px;color:${TEXT_COLOR};font-style:italic;">"Flowers are the music of the ground."</p>
      <p style="margin:8px 0 0;font-size:12px;color:#aab8a5;letter-spacing:1px;">— Edwin Curran</p>
    </div>

    <p style="font-size:14px;color:#8a9a85;text-align:center;">Thank you for trusting Arizona Roses. We'd love to be part of your next occasion.</p>

    ${ctaButton('https://order.arizonaroses.com', 'Order Again')}
  `, `Your roses have been delivered to ${order.recipient_name}!`),


  // ── 4. Forgot password ────────────────────────────────────────────────────
  forgotPassword: (resetLink) => baseTemplate(`
    <h2 style="font-family:Georgia,serif;font-size:26px;color:${TEXT_COLOR};margin:0 0 8px;font-weight:400;">Reset your password</h2>
    <p style="font-size:14px;color:#8a9a85;margin:0 0 24px;">We received a request to reset the password for your Arizona Roses account. Click the button below to choose a new one.</p>

    ${ctaButton(resetLink, 'Reset My Password')}

    ${divider()}

    <p style="font-size:12px;color:#b0bfab;text-align:center;margin:0;">
      This link expires in <strong>1 hour</strong>. If you didn't request a password reset, you can safely ignore this email — your account remains secure.
    </p>
  `, 'Reset your Arizona Roses password'),


  // ── 5. Admin — new order alert ────────────────────────────────────────────
  adminNewOrder: (order, item) => baseTemplate(`
    <h2 style="font-family:Georgia,serif;font-size:24px;color:${TEXT_COLOR};margin:0 0 8px;font-weight:400;">🌹 New Order — Action Required</h2>
    <p style="font-size:14px;color:#8a9a85;margin:0 0 20px;">A new paid order just came in and is waiting in the dashboard.</p>

    <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background:${BG_COLOR};border-radius:14px;border:2px solid ${BRAND_COLOR};margin:0 0 24px;">
      <tr><td style="padding:20px 24px;">
        <p style="margin:0 0 12px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:2px;color:${BRAND_COLOR};">New Order Details</p>
        ${row('Order ID', '#' + order.id.substring(0, 8).toUpperCase())}
        ${row('Customer', order.customer_name)}
        ${row('Email', order.customer_email)}
        ${row('Phone', order.customer_phone || '—')}
        ${row('Design', item.design_name || 'Custom')}
        ${row('Rose Color', item.color_preference || '—')}
        ${row('Delivery', new Date(order.delivery_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }))}
        ${row('Recipient', order.recipient_name)}
        ${row('Address', `${order.delivery_address}, ${order.delivery_city}, AZ ${order.delivery_zip}`)}
        ${order.special_instructions ? row('Notes', `<em style="color:${SAGE_COLOR};">"${order.special_instructions}"</em>`) : ''}
      </td></tr>
    </table>

    ${ctaButton('https://order.arizonaroses.com/#/admin', 'Open Admin Dashboard')}
  `, `New order from ${order.customer_name} — #${order.id.substring(0,8).toUpperCase()}`),

};

export { ADMIN_EMAIL };
